import React, { useEffect, useState } from 'react';
import { AppDispatch, RootState } from '../../../store/store';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Modal from '../../../components/Common/modal/Modal';
import StudentsAddNewItems from '../components/StudentsAddNewItems';
import * as StudentService from '../services/Student.service';
import { Toast } from '../../../components/Common/Toast';
import { faLock, faRefresh, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Checkbox from '../../../components/Common/Checkbox';
import Dialogs from '../../../components/Common/Dialog';
import { getURLSearchParams } from '../../../helper/helper';
import StudentEditSidebar from '../../../components/StudentEditSidebar/StudentEditSidebar';
import {
  setActiveClassroomProject,
  setSelectProjectById,
  setShowAddNewProject,
  setShowAddNewStudentsPopup,
  setShowStartTourPopup,
} from '../../../store/userSlice';
import ProjectSelector, { SelectorOption } from '../components/ProjectSelector';
import Loader from '../../../components/Common/Loader';
import { CONFIG } from '../../../config/mainConfig';
import InformNotificationPopup from '../../../components/Common/InformNotificationPopup';
import { UserLocalStorageKeysEnum } from '../../../interfaces/user.interface';
import { useFlyerGenerationGate } from '../../../hooks/useFlyerGenerationGate';

export interface TeacherClassroomStudent {
  id: string;
  username: string;
  loginName?: string;
  password: string;
  isOpen?: boolean;
  isActive?: boolean;
  bookBalance?: number;
  booksCompleted?: number;
  preOrderStudentBookCount?: number;
  bookOrdered?: boolean;
  parentReponded?: boolean;
}

export interface TeacherLibraryProps {}

const TeacherClassroomStudents: React.FC<TeacherLibraryProps> = () => {
  const dispatch: AppDispatch = useDispatch();
  const navigateTo = useNavigate();
  const searchParams = getURLSearchParams(true);
  const currentSearchQuery = useSelector((state: RootState) => state.app.currentSearchQuery);
  const user = useSelector((state: RootState) => state.user.user);
  const activeClassroom = useSelector((state: RootState) => state.user.activeClassroom);
  const activeClassroomProject = useSelector((state: RootState) => state.user.activeClassroomProject);
  const activeClassroomProjects = useSelector((state: RootState) => state.user.activeClassroomProjects);
  const showAddNewStudentsPopup = useSelector((state: RootState) => state.user.showAddNewStudentsPopup);
  const limitClassroomStudents = useSelector((state: RootState) => state.user.limitClassroomStudents);
  const isOnboarding = useSelector((state: RootState) => state.user.isOnboarding);
  const selectProjectById = useSelector((state: RootState) => state.user.selectProjectById);
  const [students, setStudents] = useState<TeacherClassroomStudent[]>([]);
  const [selectedAllStudents, setSelectedAllStudents] = useState<boolean>(false);
  const [selectedStudent, setSelectedStudent] = useState<TeacherClassroomStudent | null>(null);

  const [addNewStudentsLoader, setAddNewStudentsLoader] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [editingStudent, setEditingStudent] = useState<string | null>(null);
  const [editName, setEditName] = useState<string>('');
  const [userIdToDelete, setUserIdToDelete] = useState<string | null>(null);
  const [userIdToRefreshPassword, setUserIdToRefreshPassword] = useState<string | null>(null);
  const [userDeleteLoader, setUserDeleteLoader] = useState<boolean>(false);
  const [userRefreshPasswordLoader, setUserRefreshPasswordLoader] = useState<boolean>(false);
  const [printingPasswordLoader, setPrintingPasswordLoader] = useState<boolean>(false);
  const [addBookCreditLoader, setAddBookCreditLoader] = useState<boolean>(false);
  const preFlyerGate = useFlyerGenerationGate('pre');
  const [showStudentLimitPopup, setShowStudentLimitPopup] = useState<boolean>(false);
  const [currentStudentsLimit, setCurrentStudentsLimit] = useState<number | null>(null);
  const [studentModalPassword, setStudentModalPassword] = useState<TeacherClassroomStudent | null>(null);
  const [showStudentModalPassword, setShowStudentModalPassword] = useState<boolean>(false);
  const [isImportingStudents, setIsImportingStudents] = useState<boolean>(false);

  useEffect(() => {
    if (!students?.length) {
      return;
    }
    setSelectedAllStudents(students.every((s) => s.isActive));
  }, [students]);

  useEffect(() => {
    if (!studentModalPassword) {
      return;
    }
    if (students?.length > 0 && studentModalPassword) {
      const student = students.find((s) => s.id === studentModalPassword.id);
      if (student) {
        setStudentModalPassword({ ...student });
      }
    }
  }, [studentModalPassword, students]);

  useEffect(() => {
    if ((activeClassroomProjects?.length || -1) > 0 && !activeClassroomProject?.id) {
      const storageClassroomProjectId =
        activeClassroomProjects?.find((project) => !!project.isDefaultProject)?.id ||
        localStorage.getItem(UserLocalStorageKeysEnum.ACTIVE_CLASSROOM_PROJECT_ID);
      selectActiveClassroomProject(storageClassroomProjectId);
    }
  }, [activeClassroomProjects]);

  // useEffect(() => {
  //   if (activeClassroom?.id) {
  //     const queryParams = new URLSearchParams(currentSearchQuery);
  //     console.log(currentSearchQuery);
  //     setIsOnboarding(queryParams.get('onboarding') === 'true');
  //   }
  // }, [activeClassroom, currentSearchQuery]);

  // Fetch students on component mount
  useEffect(() => {
    if (activeClassroomProject?.id) {
      fetchStudents();
    }
  }, [activeClassroomProject]);

  useEffect(() => {
    if (selectProjectById) {
      localStorage.setItem(UserLocalStorageKeysEnum.ACTIVE_CLASSROOM_PROJECT_ID, selectProjectById);
      dispatch(setSelectProjectById(null));
    }
  }, [selectProjectById]);

  useEffect(() => {
    if (showAddNewStudentsPopup && limitClassroomStudents > 0) {
      setCurrentStudentsLimit(limitClassroomStudents - students.length);
    }
  }, [showAddNewStudentsPopup, limitClassroomStudents, students]);

  const handleOpenAddNewStudents = () => {
    if (limitClassroomStudents > 0 && students.length >= limitClassroomStudents) {
      setShowStudentLimitPopup(true);
      return;
    }
    dispatch(setShowAddNewStudentsPopup(true));
  };

  const selectActiveClassroomProject = (classroomProjectId: string | null) => {
    if (!activeClassroomProjects?.length) {
      return;
    }
    const project = activeClassroomProjects?.find((classroom) => classroom.id === classroomProjectId);
    if (project && classroomProjectId) {
      dispatch(setActiveClassroomProject({ project }));
      localStorage.setItem(UserLocalStorageKeysEnum.ACTIVE_CLASSROOM_PROJECT_ID, classroomProjectId);
    } else {
      // Prioritize default project, then first project
      const defaultClassroomProject = activeClassroomProjects.find((p) => p.isDefaultProject);
      const fallbackProject = activeClassroomProjects[0];
      const selectedProject = defaultClassroomProject || fallbackProject;

      if (selectedProject?.id) {
        dispatch(setActiveClassroomProject({ project: selectedProject }));
        localStorage.setItem(UserLocalStorageKeysEnum.ACTIVE_CLASSROOM_PROJECT_ID, selectedProject.id);
      }
    }
  };

  const fetchStudents = async (selectedStudentId?: string) => {
    if (!activeClassroom?.id || !activeClassroomProject?.id) return;

    setLoading(true);
    try {
      const response = await StudentService.getClassroomProjectStudents(
        activeClassroom?.id,
        activeClassroomProject?.id,
      );

      if (response && response.students) {
        // Transform API response to match component's data structure
        const formattedStudents = response.students.map((student: any) => ({
          id: student.email, // Using email as ID since that's what the API uses
          username: student.studentName || student.firstName,
          loginName: student.loginName,
          password: '', // We'll fetch this separately when needed
          isOpen: false,
          bookBalance: student.booksCreateLimit - (student.booksCreatedCounter || 0),
          booksCompleted: student.booksCreatedCounter,
          preOrderStudentBookCount: student.preOrderStudentBookCount,
          bookOrdered: student.bookOrdered,
          parentReponded: !!student.parentReponded,
        }));

        setStudents(formattedStudents);
        if (selectedStudentId) {
          const foundStudent = formattedStudents.find((student: any) => student.id === selectedStudentId);
          setSelectedStudent(foundStudent);
        }
      }
    } catch (error) {
      console.error('Failed to fetch students:', error);
    } finally {
      setLoading(false);
    }
  };

  const createNewStudents = async (names: string[], skipped?: boolean) => {
    if (!activeClassroom?.id) return;

    try {
      setAddNewStudentsLoader(true);
      const response = await StudentService.createStudents(activeClassroom?.id, names, !!skipped, isOnboarding, () => {
        setShowStudentLimitPopup(true);
      });

      if (response && response.students) {
        // Add new students to the state
        const newStudents = response.students.map((item: any) => ({
          id: item.student.email,
          username: item.student.studentName || item.student.firstName,
          loginName: item.student.loginName,
          password: item.password, // We get the password directly from bulk creation
          isOpen: false,
          bookBalance: item.student.booksCreateLimit - (item.student.booksCreatedCounter || 0),
          booksCompleted: item.student.booksCreatedCounter,
          preOrderStudentBookCount: item.student.preOrderStudentBookCount,
          bookOrdered: item.student.bookOrdered,
          parentReponded: !!item.student.parentReponded,
        }));

        setStudents([...students, ...newStudents]);
        dispatch(setShowAddNewStudentsPopup(false));
        if (isOnboarding) {
          dispatch(setShowStartTourPopup(true));
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setAddNewStudentsLoader(false);
    }
  };

  const togglePasswordVisibility = async (studentId: string, success?: () => void) => {
    // Find the student in our state
    const student = students.find((s) => s.id === studentId);

    if (!student) return;

    // If we don't have the password yet, fetch it
    if (!student.password && activeClassroom?.id) {
      try {
        const password = await StudentService.getStudentPassword(activeClassroom?.id, studentId);

        if (password) {
          // Update the student with the fetched password
          setStudents(students.map((s) => (s.id === studentId ? { ...s, password, isOpen: true } : s)));
        }
      } catch (error) {
        console.error('Failed to fetch password:', error);
      }
    } else {
      // Just toggle visibility if we already have the password
      setStudents(students.map((s) => (s.id === studentId ? { ...s, isOpen: !s.isOpen } : s)));
    }
    success && success();
  };

  const deleteStudentHandler = async (studentId: string) => {
    if (!activeClassroom?.id) return;
    setUserIdToDelete(studentId);
  };

  const refreshStudentPasswordHandler = async (studentId: string) => {
    if (!activeClassroom?.id) return;
    setUserIdToRefreshPassword(studentId);
  };

  const handleDeleteStudent = async () => {
    if (!activeClassroom?.id || !userIdToDelete) return;

    setUserDeleteLoader(true);
    try {
      await StudentService.deleteStudent(activeClassroom?.id, userIdToDelete);

      // Remove student from state
      setStudents(students.filter((s) => s.id !== userIdToDelete));

      // Toast('Student successfully deleted', false);
      setUserIdToDelete(null);
    } catch (e) {
      Toast('Something went wrong! Please try again', true);
    } finally {
      setUserDeleteLoader(false);
    }
  };

  const handleRefreshStudentPassword = async () => {
    if (!activeClassroom?.id || !userIdToRefreshPassword) return;

    setUserRefreshPasswordLoader(true);
    try {
      const response = await StudentService.refreshStudentPassword(activeClassroom?.id, userIdToRefreshPassword);

      const student = students.find((s) => s.id === userIdToRefreshPassword);
      if (student && response?.password) {
        // Update student password in state
        setStudents(
          students.map((s) => (s.id === userIdToRefreshPassword ? { ...s, password: response.password } : s)),
        );
      }
      setUserIdToRefreshPassword(null);
    } catch (e) {
      Toast('Something went wrong! Please try again', true);
    } finally {
      setUserRefreshPasswordLoader(false);
    }
  };

  const startEditing = (student: TeacherClassroomStudent) => {
    setEditingStudent(student.id);
    setEditName(student.username);
  };

  const saveEdit = async () => {
    if (!activeClassroom?.id || !editingStudent || !editName.trim()) return;

    try {
      await StudentService.updateStudent(activeClassroom?.id, editingStudent, {
        studentName: editName,
      });

      // Update student in state
      setStudents(students.map((s) => (s.id === editingStudent ? { ...s, username: editName } : s)));

      // Reset editing state
      setEditingStudent(null);
    } catch (error) {
      console.error('Failed to update student:', error);
    }
  };

  const printPreSessionFlyers = async () => {
    if (!activeClassroom?.id || !activeClassroomProject?.id) return;
    if (!preFlyerGate.canStart) return;

    const selectedStudents = students.filter((s) => s.isActive);
    if (selectedStudents?.length <= 0) return;

    const result = await StudentService.generatePreSessionFlyers(
      activeClassroom.id,
      activeClassroomProject?.id,
      selectedStudents?.map((item) => item.id),
    );

    if (result.status === 409) {
      Toast({
        title: 'Already generating',
        message: 'Pre-session flyer generation is already in progress.',
      });
      return;
    }
    if (result.status !== 200) {
      Toast(result.error || 'Failed to generate pre-session flyers', true);
      return;
    }
  };

  const printPasswords = async () => {
    if (!activeClassroom?.id) return;

    setPrintingPasswordLoader(true);

    let updatedStudents: TeacherClassroomStudent[] = [];

    // Determine which students to print - either selected ones or all if none selected
    const activeStudents = students.filter((s) => s.isActive);
    const studentsToPrint = activeStudents.length > 0 ? activeStudents : students;

    if (!studentsToPrint.length) {
      setPrintingPasswordLoader(false);
      return;
    }
    try {
      const passwords = await StudentService.getStudentPasswords(
        activeClassroom?.id,
        activeClassroomProject?.id,
        studentsToPrint?.map((item) => item.id || '') || [],
        true, // Set to true for printing
      );

      // Update students with fetched passwords
      updatedStudents = studentsToPrint.map((student) => {
        const updatedStudentPassword = (passwords && passwords[student.id]) || '';
        return updatedStudentPassword ? { ...student, password: updatedStudentPassword } : student;
      });

      setStudents(
        students.map((student) => {
          const updatedStudentPassword = (passwords && passwords[student.id]) || '';
          return updatedStudentPassword ? { ...student, password: updatedStudentPassword } : student;
        }),
      );
    } catch (error) {
      console.error('Failed to fetch passwords:', error);
      Toast('Failed to fetch some passwords', true);
      setPrintingPasswordLoader(false);
      return;
    }

    // Now print the passwords
    const printContent = `
      <html>
        <head>
          <title>Student Passwords</title>
          <style>
            body { font-family: Arial, sans-serif; }
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid #ddd; padding: 10px; text-align: left; font-size: 18px; }
            th { background-color: #f2f2f2; }
          </style>
        </head>
        <body>
          <h1>Student Passwords</h1>
          <table>
            <tr>
              <th>#</th>
              <th>Student Name</th>
              <th>Username</th>
              <th>Password</th>
            </tr>
            ${updatedStudents
              .map(
                (student, index) => `
              <tr>
                <td>${index + 1}</td>
                <td>${student.username}</td>
                <td>${student.loginName}</td>
                <td>${student.password || 'Not available'}</td>
              </tr>
            `,
              )
              .join('')}
          </table>
        </body>
      </html>
    `;

    // Open print window
    const printWindow = window.open('', '_blank');
    printWindow?.document.write(printContent);
    printWindow?.document.close();
    printWindow?.print();
    setPrintingPasswordLoader(false);
  };

  const handleEditClick = (student: TeacherClassroomStudent) => {
    setSelectedStudent(student);
  };

  const handleAddBookCredit = async (student: TeacherClassroomStudent) => {
    if (!activeClassroom?.id) return;

    setAddBookCreditLoader(true);
    try {
      await StudentService.incrementStudentBookCount(activeClassroom.id, student.id, () => {
        fetchStudents(student.id);
      });

      Toast('Book credit added successfully', false);
    } catch (error) {
      console.error('Failed to add book credit:', error);
      Toast('Failed to add book credit', true);
    } finally {
      setAddBookCreditLoader(false);
    }
  };

  const selectStudent = (student?: TeacherClassroomStudent, idx?: number) => {
    if (!student) {
      const activeStudents = students.filter((s) => s.isActive);
      const changedStudents = students.map((item) => ({
        ...item,
        isActive: activeStudents?.length !== students.length,
      }));
      setStudents(changedStudents);
      setSelectedAllStudents(changedStudents.every((s) => s.isActive));
    } else {
      const changedStudents = students.map((item, i) => (i !== idx ? item : { ...item, isActive: !student.isActive }));
      setStudents(changedStudents);
      setSelectedAllStudents(changedStudents.every((s) => s.isActive));
    }
  };

  return (
    <div className="min-h-[calc(100vh-10rem)] flex flex-col font-sofiaSans max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-3 sm:pb-4 lg:pb-6">
      {/* Header */}
      <div className="flex flex-col mt-8 justify-start">
        <div className="flex">
          <h1 className="text-xl sm:text-2xl font-bold text-storySecondaryBlueText">Student's</h1>
        </div>
        <div className="flex flex-col lg:flex-row w-full justify-between gap-4 mt-4 sm:mt-8">
          <div className="flex flex-row justify-between sm:justify-start sm:gap-4">
            <div className="flex sm:gap-4 flex-nowrap">
              <button
                className="teacher-tour-print-passwords-button relative text-secondaryStoryAdditionalLightBlueText bg-transparent sm:border border-secondaryStoryAdditionalLightBlueText rounded-2xl hover:shadow-md px-2 sm:px-4 py-2 text-sm font-bold flex gap-2 items-center h-9 whitespace-nowrap"
                onClick={printPasswords}
              >
                {/* Print Icon */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="md:hidden lg:flex w-7 h-7 sm:w-6 sm:h-6"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6.75 9V5.25A2.25 2.25 0 019 3h6a2.25 2.25 0 012.25 2.25V9"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6.75 15.75v2.25A2.25 2.25 0 009 20.25h6a2.25 2.25 0 002.25-2.25v-2.25"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3.75 9.75h16.5a.75.75 0 01.75.75v6a.75.75 0 01-.75.75H3.75a.75.75 0 01-.75-.75v-6a.75.75 0 01.75-.75z"
                  />
                </svg>
                <span className="hidden md:inline">Print Passwords</span>
                <span className="hidden sm:flex md:hidden">Print</span>
                {printingPasswordLoader && (
                  <div className="flex items-center ml-1 pr-4">
                    <Loader loader={true} className={'absolute right-0'} dimentions={'w-9 h-9'} />
                  </div>
                )}
              </button>
              {(() => {
                const hasStudentSelected = students.some((s) => s.isActive);
                const preFlyerDisabled = !preFlyerGate.canStart || !hasStudentSelected;
                const preFlyerTitle = preFlyerGate.isRunning
                  ? 'Pre-session flyer generation is already in progress. Check the status panel.'
                  : !hasStudentSelected
                    ? 'Select at least one student to generate flyers'
                    : 'Generate Pre-session Flyer';
                return (
                  <button
                    type="button"
                    disabled={preFlyerDisabled}
                    className={`teacher-tour-pre-session-flyers-button text-secondaryStoryAdditionalLightBlueText bg-transparent sm:border border-secondaryStoryAdditionalLightBlueText rounded-2xl hover:shadow-md px-2 sm:px-4 py-1 text-sm font-bold flex gap-2 items-center h-9 whitespace-nowrap relative ${
                      preFlyerDisabled ? 'opacity-50 cursor-not-allowed hover:shadow-none' : ''
                    }`}
                    onClick={() => !preFlyerDisabled && printPreSessionFlyers()}
                    title={preFlyerTitle}
                  >
                    <div className="flex items-center">
                      <img className={`md:hidden lg:flex w-9 h-9 sm:w-8 sm:h-8`} src="/flyer-icon.png" alt="flyer" />
                      <span className="hidden md:inline">Pre-Session Flyer</span>
                      <span className="hidden sm:flex md:hidden">Flyer</span>
                    </div>
                  </button>
                );
              })()}
            </div>

            <div className="flex flex-row gap-2 sm:gap-4 lg:gap-6">
              <div className="flex items-center">
                {activeClassroom?.groupCode && (
                  <div className="teacher-tour-group-code-container flex flex-col relative sm:flex-row sm:items-center gap-1 sm:gap-0">
                    <div className="absolute md:relative -top-2 md:top-auto left-2 md:left-auto text-gray-600 md:text-black bg-whiteGrayBackground md:bg-transparent flex items-center flex-nowrap whitespace-nowrap font-bold pr-1.5 text-xs md:text-sm">
                      Group Code<span className="hidden md:flex ml-1"> -</span>
                    </div>
                    <div className="flex border-secondaryStoryLightBlueText border-2 border-opacity-20 rounded-lg px-3 sm:px-4 py-1.5 gap-2 xl:gap-6">
                      <div className="flex items-center font-bold text-secondaryStoryBlueText text-sm sm:text-base">
                        {activeClassroom?.groupCode}
                      </div>
                      <div
                        className="flex items-center gap-0.5 xl:gap-2 cursor-pointer text-xs sm:text-sm"
                        onClick={() => {
                          navigator.clipboard.writeText(activeClassroom?.groupCode || '');
                          Toast('Group code copied to clipboard', false);
                        }}
                      >
                        <span className="hidden xl:flex">Copy</span>
                        <img
                          className="w-4 h-4 sm:w-6 sm:min-w-6 sm:h-6"
                          src="/copy-icon.png"
                          alt="copy"
                          title="copy"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-0">
                <div className="hidden lg:flex items-center flex-nowrap whitespace-nowrap font-bold pr-1.5 text-xs sm:text-sm">
                  Project -
                </div>
                <ProjectSelector
                  placeholder="Choose project"
                  selectedItem={activeClassroomProject?.id || ''}
                  onChange={(data: SelectorOption) => selectActiveClassroomProject(data?.value)}
                  options={
                    activeClassroomProjects?.map((item) => ({
                      title: item.name || '',
                      value: item.id || '',
                      isDefaultProject: item.isDefaultProject,
                    })) || []
                  }
                  addNewItemTitle={'Add new project'}
                  addNewItemHandler={() => dispatch(setShowAddNewProject(true))}
                ></ProjectSelector>
              </div>
            </div>
          </div>
          <button
            className="teacher-tour-add-student-button text-white bg-storyblueSecondLightText border border-storyblueSecondLightText rounded-2xl hover:shadow-md px-3 sm:px-4 py-2 text-xs sm:text-sm font-bold flex gap-2 items-center h-9 justify-center lg:justify-start whitespace-nowrap mt-4 lg:mt-0"
            onClick={() => handleOpenAddNewStudents()}
          >
            {/* Plus Icon */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={3}
              stroke="currentColor"
              className="w-4 h-4 sm:w-5 sm:h-5"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
            Add Student's
          </button>
        </div>
      </div>

      {/* VPC Status for Illinois Teachers */}
      {/*{activeClassroom?.id && (*/}
      {/*  <div className="mt-6">*/}
      {/*    <ClassroomVPCStatus classroomId={activeClassroom.id} />*/}
      {/*  </div>*/}
      {/*)}*/}

      {/* Table */}
      <div className="bg-white rounded-xl shadow mt-8 p-3 sm:p-6 overflow-x-auto">
        {loading ? (
          <div className="text-center py-8">Loading students...</div>
        ) : students.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500">No students found</p>
            <button
              className="mt-4 text-storyblueSecondLightText hover:underline"
              onClick={() => dispatch(setShowAddNewStudentsPopup(true))}
            >
              Add your first student
            </button>
          </div>
        ) : (
          <div className="md:min-w-[600px]">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="hidden sm:block text-left py-2 px-2 sm:px-4 font-bold text-xs sm:text-sm">Number</th>
                  <th className="block sm:hidden text-left py-2 px-2 sm:px-4 font-bold text-xs sm:text-sm">#</th>
                  <th className="text-left py-2 px-2 sm:px-4 font-bold text-xs sm:text-sm">
                    <span className="hidden sm:block pr-1">Student</span>Username
                  </th>
                  <th className="text-left py-2 px-2 sm:px-4 font-bold text-xs sm:text-sm">
                    <span className="hidden sm:block">Password</span>
                  </th>
                  <th className="text-left py-2 px-2 sm:px-4 font-bold text-xs sm:text-sm max-w-16">
                    <span className="hidden sm:block">Parent Responded</span>
                  </th>
                  <th className="text-left py-2 px-2 sm:px-4 font-bold text-xs sm:text-sm max-w-16">
                    <span className="hidden sm:block">Book Ordered</span>
                  </th>
                  <th className="py-2 px-2 sm:px-4">
                    <div className="flex justify-end items-end sm:items-start md:items-center gap-2 flex-col md:flex-row">
                      <span className="hidden sm:block text-xs sm:text-sm font-bold">
                        Select All ({students.length})
                      </span>
                      {/* Checkbox - Select all */}
                      <Checkbox
                        checked={selectedAllStudents}
                        onCheckedChange={() => selectStudent()}
                        title={'Select All Students'}
                        className={`min-w-5 w-5 h-5 sm:w-6 sm:h-6 ml-2 z-2`}
                        style={{ zIndex: 1 }}
                      />
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {students.map((student, idx) => (
                  <tr key={student.id} className="border-b last:border-b-0 hover:bg-gray-50">
                    <td className="py-3 px-2 sm:px-4 font-bold text-secondaryStoryAdditionalLightBlueText text-sm sm:text-lg text-left">
                      {idx + 1}
                    </td>
                    <td className="py-3 px-2 sm:px-4 w-full sm:w-auto">
                      {editingStudent === student.id ? (
                        <div className="flex flex-col gap-2">
                          <input
                            type="text"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            className="border rounded px-3 py-1 text-sm"
                            autoFocus
                          />
                          <div className="flex gap-1">
                            <button onClick={saveEdit} className="p-1 bg-green-100 hover:bg-green-200 rounded text-xs">
                              Save
                            </button>
                            <button
                              onClick={() => setEditingStudent(null)}
                              className="p-1 bg-gray-100 hover:bg-gray-200 rounded text-xs"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-col items-start w-auto min-w-0">
                          <span
                            className="text-secondaryStoryAdditionalLightBlueText font-bold cursor-pointer hover:underline text-sm sm:text-base"
                            onClick={() => handleEditClick(student)}
                          >
                            {student.loginName || student.username}
                          </span>
                          <span
                            className="text-xs text-secondaryStoryAdditionalLightBlueText flex items-center gap-1 cursor-pointer"
                            onClick={() => handleEditClick(student)}
                          >
                            Open <span>&rarr;</span>
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-2 sm:px-4">
                      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
                        <div className="hidden sm:flex items-center gap-2 relative w-full sm:w-auto">
                          <input
                            type="text"
                            value={
                              student.isOpen
                                ? student.password || 'Loading...'
                                : student.password
                                  ? Array.from({ length: student.password.length }, (_) => '*').join('')
                                  : '********'
                            }
                            readOnly
                            className="border rounded px-3 py-1 w-full sm:w-40 bg-gray-100 text-gray-700 focus:outline-none pr-8 text-sm"
                          />
                          {/* Eye Icon */}
                          <button
                            className="p-1 hover:bg-gray-200 rounded absolute right-0.5 top-0 bottom-0"
                            onClick={() => togglePasswordVisibility(student.id)}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={1.5}
                              stroke={student.isOpen ? '#d9d9d9' : '#000000'}
                              className="w-4 h-4 sm:w-5 sm:h-5"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M2.25 12s3.75-7.5 9.75-7.5 9.75 7.5 9.75 7.5-3.75 7.5-9.75 7.5S2.25 12 2.25 12z"
                              />
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z"
                              />
                            </svg>
                          </button>
                        </div>

                        <button
                          className="flex sm:hidden p-1 bg-secondaryStoryBackgroundGray hover:bg-secondaryStoryGrayText rounded-lg"
                          title="Show user password"
                          onClick={() => {
                            setStudentModalPassword(student);
                            togglePasswordVisibility(student.id);
                            setShowStudentModalPassword(true);
                          }}
                        >
                          <FontAwesomeIcon
                            icon={faLock}
                            className="text-lg sm:text-xl text-secondaryStoryAdditionalLightBlueText p-1 cursor-pointer"
                          />
                        </button>

                        {/* Copy Icon */}
                        {student.password && (
                          <button
                            className="hidden sm:flex p-1 bg-secondaryStoryBackgroundGray hover:bg-secondaryStoryGrayText rounded-lg"
                            onClick={() => {
                              navigator.clipboard.writeText(student.password);
                              Toast('Password copied to clipboard', false);
                            }}
                          >
                            <img className="w-5 h-5 sm:w-6 sm:h-6" src="/copy-icon.png" alt="copy" title="copy" />
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-2 sm:px-4 w-full sm:w-auto">
                      <div className="flex items-center justify-center md:justify-start">
                        {student?.parentReponded ? (
                          <img
                            src="/controls/accept-light-green.svg"
                            alt="yes"
                            title="Student has parent repond"
                            className="h-5 w-5"
                          />
                        ) : (
                          <img
                            src="/controls/decline-light-gray.svg"
                            alt="no"
                            title="Student doesn't have parent repond"
                            className="h-5 w-5"
                          />
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-2 sm:px-4 w-full sm:w-auto justify-center md:justify-start">
                      <div className="flex items-center justify-center md:justify-start">
                        {!!student?.bookOrdered ? (
                          <img
                            src="/controls/accept-light-green.svg"
                            alt="yes"
                            title="Student has pre-order books"
                            className="h-5 w-5"
                          />
                        ) : (
                          <img
                            src="/controls/decline-light-gray.svg"
                            alt="no"
                            title="Student doesn't have pre-order books"
                            className="h-5 w-5"
                          />
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-2 sm:px-4">
                      <div className="flex items-center justify-end gap-2 sm:gap-3 pl-0.5">
                        {/* Delete Icon */}
                        <button
                          className="p-1 hover:bg-gray-200 rounded flex justify-center items-center"
                          onClick={() => refreshStudentPasswordHandler(student.id)}
                        >
                          <FontAwesomeIcon
                            title="Refresh student password"
                            icon={faRefresh}
                            onClick={() => {}}
                            className="text-lg sm:text-xl text-secondaryStoryLightBlueText p-1 cursor-pointer"
                          />
                        </button>
                        <button
                          className="p-1 hover:bg-gray-200 rounded flex justify-center items-center"
                          onClick={() => deleteStudentHandler(student.id)}
                        >
                          <FontAwesomeIcon
                            title="Edit user"
                            icon={faTrashAlt}
                            onClick={() => {}}
                            className="text-lg sm:text-xl text-storyseconderror p-1 cursor-pointer"
                          />
                        </button>
                        {/* Checkbox */}
                        <Checkbox
                          checked={!!student.isActive}
                          onCheckedChange={() => selectStudent(student, idx)}
                          title={'Only print for this  student'}
                          className={`w-5 h-5 sm:w-6 sm:h-6 ml-2 z-2`}
                          style={{ zIndex: 1 }}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {!!showAddNewStudentsPopup && (
        <Modal
          // title={`Add students`}
          // description={`Add students, each on a new line. You dont need to use their full name, as long as you can tell them apart. We don't share this list with anyone.`}
          className={'global-absolute-popup-style font-sofiaSans secondary-small'}
          mainContainerClassName={'sm:max-w-[475px]'}
          descriptionClassContainer={'md:w-full'}
          descriptionClassName={'text-storyblackText font-sofiaSans font-medium'}
          showSubmitButton={false}
          showCloseButton={false}
          showTopRightCloseButton={!isImportingStudents}
          showCloseButtonFunction={() => dispatch(setShowAddNewStudentsPopup(false))}
        >
          <StudentsAddNewItems
            loader={addNewStudentsLoader}
            isOnboarding={isOnboarding}
            limitClassroomStudents={currentStudentsLimit}
            skip={() => {
              createNewStudents([], true);
              dispatch(setShowAddNewStudentsPopup(false));
              navigateTo(`/classroom/students?onboarding=true`);
              dispatch(setShowStartTourPopup(true));
            }}
            back={() => window.history.back()}
            close={() => {
              dispatch(setShowAddNewStudentsPopup(false));
              navigateTo(`/classroom/students?onboarding=${isOnboarding}`);
            }}
            submit={(names: string[]) => createNewStudents(names)}
            limitClassroomStudentsFeedback={() => setShowStudentLimitPopup(true)}
            classroomId={activeClassroom?.id}
            projectId={activeClassroomProject?.id}
            onStudentsImported={() => {
              fetchStudents();
              navigateTo(`/classroom/students?onboarding=${isOnboarding}`);
              if (isOnboarding) {
                dispatch(setShowStartTourPopup(true));
              }
            }}
            onImportingFlow={(isImporting) => setIsImportingStudents(isImporting)}
          ></StudentsAddNewItems>
        </Modal>
      )}
      <Dialogs
        openDialog={!!userIdToDelete}
        handelClose={() => setUserIdToDelete(null)}
        handelProcess={handleDeleteStudent}
        loader={userDeleteLoader}
        confirmMessage="Are you sure you want to delete this student?"
      />
      <Dialogs
        openDialog={!!userIdToRefreshPassword}
        handelClose={() => setUserIdToRefreshPassword(null)}
        handelProcess={handleRefreshStudentPassword}
        loader={userRefreshPasswordLoader}
        confirmMessage="Are you sure you want to refresh password for this student?"
      />

      {/* Student Edit Sidebar */}
      {selectedStudent && (
        <StudentEditSidebar
          student={selectedStudent}
          onClose={() => setSelectedStudent(null)}
          onAddBookCredit={() => handleAddBookCredit(selectedStudent)}
          project="1"
          addBookCreditLoader={addBookCreditLoader}
        />
      )}

      <InformNotificationPopup
        isOpen={showStudentLimitPopup}
        onClose={() => setShowStudentLimitPopup(false)}
        title={'Enterprise'}
      >
        <div className="flex flex-col text-storyblackText font-sofiaSans md:max-w-md">
          <p className="mb-6">
            If you would like more than {limitClassroomStudents} student’s please contact us for a free enterprise
            account.
          </p>
          <div className="flex items-end">
            <div className="flex flex-col">
              <span>Email us at:</span>
              <span className="font-black">{CONFIG.ADMIN_EMAIL_ADDRESS}</span>
            </div>
            <button
              onClick={() => {
                navigator.clipboard.writeText(CONFIG.ADMIN_EMAIL_ADDRESS).then(() => {
                  Toast('Email copied successfully', false);
                });
              }}
              aria-label="Copy"
              title="Copy email address"
              className="hover:opacity-80 transition-opacity bg-[#f5f5f4] rounded-lg p-1 ml-4"
            >
              <img src="/controls/copy-text-icon.png" alt="" className="w-6" />
            </button>
          </div>
        </div>
      </InformNotificationPopup>

      {showStudentModalPassword && (
        <Modal
          title={`${studentModalPassword?.loginName} password!`}
          description={``}
          className={'global-absolute-popup-style font-sofiaSans secondary-small'}
          mainContainerClassName={'sm:max-w-[450px] secondary-small-popup w-full sm:w-auto'}
          descriptionClassContainer={'w-full'}
          descriptionClassName={'text-storyblackText font-sofiaSans font-medium'}
          showSubmitButton={false}
          showCloseButton={false}
          showTopRightCloseButton={true}
          showCloseButtonFunction={() => {
            setStudentModalPassword(null);
            setShowStudentModalPassword(false);
          }}
        >
          <div className="flex w-full justify-center mt-6 mb-4">
            <div className="flex flex-col items-center gap-2 relative w-full sm:w-auto">
              <h2 className="font-bold text-xl sm:text-base">
                {studentModalPassword?.password ? studentModalPassword?.password : 'Loading...'}
              </h2>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(studentModalPassword?.password || '');
                  Toast('Group code copied to clipboard', false);
                }}
                className="text-secondaryStoryAdditionalLightBlueText bg-transparent border border-secondaryStoryAdditionalLightBlueText rounded-2xl hover:shadow-md p-2 text-sm font-bold flex items-center h-9"
              >
                Copy
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default TeacherClassroomStudents;
